import { DRIVE_UNIT_PORT_MAX, DRIVE_UNIT_PORT_MIN } from './driveUnitPortSelection';
import { getFrameworkMechanismClassNames } from './frameworkClassNameSets';
import { escapeRegex } from './monacoRegionUtils';
import {
  collectLastTopLevelVarAssignmentsByName,
  findConstructorCallEndLineIndex,
  findMatchingDelimiterIndex,
  isTopLevelCodeLine,
  offsetAtLineColumn,
  splitCodeLines,
} from './pythonSourceScanUtils';

const FOLLOWER_MOTOR_PORT_ELECTRONIC_RE = /:follower_motor_port_(\d+)$/;

export type MechanismFollowerMotorFromCode = {
  mechanismVariableName: string;
  mechanismClassName: string;
  port: number;
  /** User-authored name from `add_motor(..., name="...")` (not localized). */
  name: string;
};

function unescapePythonStringLiteral(inner: string, quote: string): string {
  return inner
    .replace(/\\\\/g, '\\')
    .replace(new RegExp(`\\\\${quote}`, 'g'), quote)
    .replace(/\\n/g, '\n')
    .replace(/\\t/g, '\t');
}

/** Parses a supported `mechanism.add_motor(...)` call snippet. */
export function parseAddMotorCallFromSource(rawCode: string): { port: number; name: string } | null {
  const portValueMatch = rawCode.match(/\bport\s*=\s*([^,)]+)/);
  const portText = portValueMatch?.[1]?.trim();
  if (!portText || !/^\d+$/.test(portText)) return null;
  const port = Number.parseInt(portText, 10);
  if (!Number.isFinite(port)) return null;

  const nameMatch = rawCode.match(/\bname\s*=\s*("([^"\\]|\\.)*"|'([^'\\]|\\.)*')/);
  if (/\bname\s*=/.test(rawCode) && !nameMatch) return null;

  let name = '';
  const rawName = nameMatch?.[1];
  if (rawName && rawName.length >= 2) {
    const quote = rawName[0];
    name = unescapePythonStringLiteral(rawName.slice(1, -1), quote);
  }

  return { port, name };
}

/** Top-level `add_motor` call source snippets for a mechanism after `startLineIndex`. */
export function collectMechanismAddMotorCallSources(lines: string[], mechanismName: string, startLineIndex: number): string[] {
  const addMotorCallRe = new RegExp(`^${escapeRegex(mechanismName)}\\.add_motor\\s*\\(`);
  const sources: string[] = [];
  const code = lines.join('\n');

  for (let lineIndex = Math.max(0, startLineIndex); lineIndex < lines.length; lineIndex++) {
    const line = lines[lineIndex] ?? '';
    if (!isTopLevelCodeLine(line)) continue;
    const callMatch = addMotorCallRe.exec(line);
    if (!callMatch) continue;

    const openParenIndex = callMatch.index + callMatch[0].length - 1;

    const openOffset = offsetAtLineColumn(lines, lineIndex, openParenIndex);
    const closeOffset = findMatchingDelimiterIndex(code, openOffset, '(', ')', { ignoreLineComments: true });
    const endLineIndex = closeOffset >= 0 ? code.slice(0, closeOffset + 1).split(/\r?\n/).length - 1 : lineIndex;
    sources.push(
      lines
        .slice(lineIndex, endLineIndex + 1)
        .join('\n')
        .trim()
    );
    lineIndex = endLineIndex;
  }

  return sources;
}

/** Follower/boost motors declared via `mechanism.add_motor(...)` in program code. */
export function parseMechanismFollowerMotorsFromProjectCode(projectCode: string): MechanismFollowerMotorFromCode[] {
  if (!projectCode.trim()) return [];

  const lines = splitCodeLines(projectCode);
  const results: MechanismFollowerMotorFromCode[] = [];

  for (const { lineIndex, variableName: mechanismVariableName, className: mechanismClassName } of collectLastTopLevelVarAssignmentsByName(lines, {
    allowedClassNames: getFrameworkMechanismClassNames(),
  })) {
    const constructorEndLineIndex = findConstructorCallEndLineIndex(lines, lineIndex);
    const callSources = collectMechanismAddMotorCallSources(lines, mechanismVariableName, constructorEndLineIndex + 1);

    for (const rawCode of callSources) {
      const parsed = parseAddMotorCallFromSource(rawCode);
      if (!parsed) continue;
      if (parsed.port < DRIVE_UNIT_PORT_MIN || parsed.port > DRIVE_UNIT_PORT_MAX) continue;
      results.push({
        mechanismVariableName,
        mechanismClassName,
        port: parsed.port,
        name: parsed.name,
      });
    }
  }

  return results;
}

export function followerMotorPortElectronic(cardId: string, port: number): string {
  return `${cardId}:follower_motor_port_${port}`;
}

export function isFollowerMotorPortElectronic(electronic: string): boolean {
  return FOLLOWER_MOTOR_PORT_ELECTRONIC_RE.test(electronic);
}
